"use client";

import { useMemo, useState } from "react";
import {
  EMPTY_TRAIL_FILTERS,
  matchesTrailFacets,
  type TrailCardModel,
  type TrailFacets,
  type TrailFilterGroup,
  type TrailFilterState,
} from "@/lib/trail-view";
import { TrailCard } from "./TrailCard";

export type TrailListItem = { card: TrailCardModel; facets: TrailFacets };

/**
 * Trail discovery.
 *
 * The filter groups are computed on the server from the trails actually
 * present, so this component never decides what to offer — it only decides
 * what is selected. A collection where nobody has verified a duration simply
 * receives no "Time on foot" group and renders one fewer fieldset.
 *
 * Filters are real checkboxes inside real fieldsets. That gets keyboard
 * support, focus rings, group labelling and the selected state announced by
 * screen readers from the platform rather than from re-implemented ARIA on a
 * row of divs.
 */
export function TrailsExplorer({
  items,
  groups,
}: {
  items: TrailListItem[];
  groups: TrailFilterGroup[];
}) {
  const [filters, setFilters] = useState<TrailFilterState>(EMPTY_TRAIL_FILTERS);

  const visible = useMemo(
    () => items.filter((item) => matchesTrailFacets(item.facets, filters)),
    [items, filters],
  );

  const activeCount =
    filters.difficulty.length +
    filters.routeType.length +
    filters.bestFor.length +
    filters.duration.length;

  function toggle(groupKey: keyof TrailFilterState, rawValue: string) {
    setFilters((prev) => {
      // difficulty is the one numeric facet; everything else is a string key.
      const value: string | number = groupKey === "difficulty" ? Number(rawValue) : rawValue;
      const current = prev[groupKey] as Array<string | number>;
      const next = current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value];
      return { ...prev, [groupKey]: next } as TrailFilterState;
    });
  }

  function isChecked(groupKey: keyof TrailFilterState, rawValue: string) {
    const value: string | number = groupKey === "difficulty" ? Number(rawValue) : rawValue;
    return (prevIncludes(filters[groupKey] as Array<string | number>, value));
  }

  return (
    <div>
      {groups.length > 0 ? (
        <div className="mb-8 rounded-lg border border-[var(--border-subtle)] bg-[var(--surface)] p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h2 className="m-0 text-[0.78rem] font-bold tracking-[0.16em] uppercase text-[var(--muted)]">
              Narrow the List
            </h2>
            {activeCount > 0 ? (
              <button
                type="button"
                onClick={() => setFilters(EMPTY_TRAIL_FILTERS)}
                className="cursor-pointer border-0 bg-transparent p-0 text-[0.82rem] font-semibold text-[var(--cta)] underline underline-offset-4"
              >
                Clear {activeCount} filter{activeCount === 1 ? "" : "s"}
              </button>
            ) : null}
          </div>

          <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {groups.map((group) => (
              <fieldset key={group.key} className="m-0 min-w-0 border-0 p-0">
                <legend className="mb-2 p-0 text-[0.72rem] font-bold tracking-[0.14em] uppercase text-[var(--muted)]">
                  {group.label}
                </legend>
                <div className="flex flex-wrap gap-2">
                  {group.options.map((option) => {
                    const checked = isChecked(group.key, option.value);
                    return (
                      <label
                        key={option.value}
                        className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-[0.8rem] font-medium transition-colors duration-150 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[var(--cta)] ${
                          checked
                            ? "border-[var(--cta)] bg-[var(--cta)] text-[var(--background)]"
                            : "border-[var(--border-subtle)] text-[var(--foreground)] hover:border-[color-mix(in_srgb,var(--cta)_50%,transparent)]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="sr-only"
                          checked={checked}
                          onChange={() => toggle(group.key, option.value)}
                        />
                        {option.label}
                        <span className="tabular-nums opacity-70">{option.count}</span>
                      </label>
                    );
                  })}
                </div>
              </fieldset>
            ))}
          </div>
        </div>
      ) : null}

      {/* Live region: filtering happens without navigation, so the result
          count has to be announced rather than merely re-rendered. */}
      <p aria-live="polite" className="mb-5 text-sm text-[var(--muted)]">
        {visible.length} trail{visible.length === 1 ? "" : "s"}
        {activeCount > 0 ? ` matching your filters, of ${items.length}` : null}
      </p>

      {visible.length > 0 ? (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((item) => (
            <TrailCard key={item.card.slug} trail={item.card} />
          ))}
        </div>
      ) : (
        <div className="rounded-lg border border-dashed border-[var(--border-subtle)] p-10 text-center">
          <p className="m-0 text-[1.05rem] font-medium text-[var(--foreground)]">
            No trails match that combination.
          </p>
          <p className="mx-auto mt-2 mb-0 max-w-[42ch] text-sm leading-relaxed text-[var(--muted)]">
            Filters only cover what we have verified. Clearing one may bring back trails whose
            figures nobody has published yet.
          </p>
        </div>
      )}
    </div>
  );
}

function prevIncludes(list: Array<string | number>, value: string | number): boolean {
  return list.includes(value);
}
